/**
 * The selection model — **two modes, because "select all" means the filter and
 * not the window.**
 *
 * `components.md`: *"header checkbox selects **the whole filter**, not the
 * loaded window. Count is server-side and authoritative."*
 *
 * That sentence cannot be honoured by a `Set` of ids. The loaded window is a
 * hundred rows out of nine hundred; ticking the header and collecting the ids
 * that happen to be in memory produces a selection that *looks* like select-all
 * and silently means "the first hundred" — the same class of defect as counting
 * the dashboard's statuses out of a page, one screen over.
 *
 * So a selection is one of two things:
 *
 *   - **`rows`** — these specific ids, and nothing else. What clicking builds.
 *   - **`filter`** — everything the current filter matches, *minus* whatever the
 *     user has since unticked. What the header checkbox builds. It names no ids
 *     at all, which is what lets it mean nine hundred rows while a hundred are
 *     loaded.
 *
 * ## The count is the server's, and its absence is a third state
 *
 * {@link selectionSize} returns `null` for a filter selection whose total is not
 * known yet. Not zero, and not the loaded row count: a bar that renders `0
 * selected` while the user has just selected everything is worse than one that
 * waits, and a bar that renders `100 selected` is worse than both because it is
 * wrong and confident. The caller shows a waiting state for `null`.
 *
 * ## Changing the filter discards the selection, and the caller must do it
 *
 * A `filter` selection means *"whatever the current filter matches"*, so it
 * describes a different set the instant the query changes. There is no way for
 * this module to notice that — it never sees the filter — which is why the rule
 * is stated here and enforced at the call site, where the query lives.
 */
export const EMPTY_SELECTION = { mode: "rows", ids: new Set() };
/** Everything the current filter matches. Carries no ids by construction. */
export function selectWholeFilter() {
    return { mode: "filter", excluded: new Set() };
}
export function clearSelection() {
    return EMPTY_SELECTION;
}
export function isRowSelected(selection, id) {
    return selection.mode === "rows" ? selection.ids.has(id) : !selection.excluded.has(id);
}
/**
 * Tick or untick one row, staying in whichever mode the selection is in.
 *
 * Unticking inside a filter selection **does not** collapse it to a row
 * selection: "everything except this one" is the state the user expressed, and
 * turning it into a list of ids would silently drop every row not yet loaded.
 */
export function toggleRow(selection, id) {
    if (selection.mode === "rows") {
        const ids = new Set(selection.ids);
        if (!ids.delete(id))
            ids.add(id);
        return { mode: "rows", ids };
    }
    const excluded = new Set(selection.excluded);
    if (!excluded.delete(id))
        excluded.add(id);
    return { mode: "filter", excluded };
}
/** Replace the selection with exactly these ids. What a shift-extend produces. */
export function selectRows(ids) {
    return { mode: "rows", ids: new Set(ids) };
}
/**
 * How many rows are selected, or `null` when the server's count is not in yet.
 *
 * `total` is the **filter count from the API** — `ProductPage.total`, never
 * `rows.length`. Passing the loaded row count here would make this function
 * return the exact lie it exists to prevent, so the parameter is named for what
 * it must be and the call sites are few enough to read.
 */
export function selectionSize(selection, total) {
    if (selection.mode === "rows")
        return selection.ids.size;
    if (total === null)
        return null;
    // `max(0, …)` because the two numbers come from different moments: a row
    // deleted elsewhere between the count and the untick would otherwise render a
    // negative selection.
    return Math.max(0, total - selection.excluded.size);
}
/** True when nothing is selected — the state in which no bulk bar renders. */
export function isSelectionEmpty(selection, total) {
    return selectionSize(selection, total) === 0;
}
//# sourceMappingURL=selection.js.map